import { useQueries } from '@tanstack/react-query';
import { useApiClient } from '../../api/client.jsx';
import { useBrand } from '../../brand/BrandProvider';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import { createDebugLogger } from '../../utils/debug';
import { brandStatsFixture } from '../BrandDashboard/fixture';

const dlog = createDebugLogger('admin-overview');

/**
 * Brand-übergreifende System-Stats.
 *
 * Iteriert über alle Brands die der Operator sieht (`brand.brands`),
 * zieht pro Brand das `/v1/admin/stats`-Snapshot via Brand-Origin-Override
 * im API-Client (siehe client.jsx: `hasExplicit` short-circuit).
 *
 * Liefert einzelne Per-Brand-Snapshots und ein clientseitiges Aggregat.
 */
export function useSystemStats() {
  const { fetch } = useApiClient();
  const brand = useBrand();
  const fixturesMode = useFixturesMode();

  const brands = brand.brands ?? [];

  const queries = useQueries({
    queries: brands.map((b) => ({
      queryKey: ['admin-overview', 'stats', b.brandOrigin, fixturesMode ? 'fixture' : 'live'],
      queryFn: async () => {
        if (fixturesMode) {
          await fixtureDelay();
          return { brand: b, stats: normalizeStats(brandStatsFixture()) };
        }
        const res = await fetch('/v1/admin/stats', {
          headers: { 'Brand-Origin': b.brandOrigin },
        });
        dlog(`/v1/admin/stats raw [${b.brandOrigin}]`, res);
        return { brand: b, stats: normalizeStats(res) };
      },
      staleTime: 60_000,
    })),
  });

  const items = queries.map((q, i) => ({
    brand: brands[i],
    stats: q.data?.stats,
    isLoading: q.isLoading,
    error: q.error,
  }));

  const aggregate = items.reduce(
    (acc, item) => {
      if (!item.stats) return acc;
      acc.revenue.paidCount += item.stats.revenue.paidCount;
      acc.revenue.paidTotal += item.stats.revenue.paidTotal;
      acc.revenue.totalOrders += item.stats.revenue.totalOrders;
      acc.users.total += item.stats.users.total;
      acc.users.active += item.stats.users.active;
      acc.users.suspended += item.stats.users.suspended;
      acc.services.total += item.stats.services.total;
      acc.services.active += item.stats.services.active;
      acc.services.suspended += item.stats.services.suspended;
      acc.services.failed += item.stats.services.failed;
      acc.brandCount += 1;
      return acc;
    },
    {
      revenue: { paidCount: 0, paidTotal: 0, totalOrders: 0 },
      users: { total: 0, active: 0, suspended: 0 },
      services: { total: 0, active: 0, suspended: 0, failed: 0 },
      brandCount: 0,
    },
  );

  return {
    items,
    aggregate,
    isLoading: queries.some((q) => q.isLoading),
    hasError: queries.some((q) => q.error),
  };
}

function normalizeStats(raw) {
  if (!raw) return null;
  const num = (v) => {
    if (v == null) return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  return {
    revenue: {
      paidCount: num(raw.revenue?.paidCount),
      paidTotal: num(raw.revenue?.paidTotal),
      totalOrders: num(raw.revenue?.totalOrders),
    },
    users: {
      total: num(raw.users?.totalUsers ?? raw.users?.total),
      active: num(raw.users?.activeUsers ?? raw.users?.active),
      suspended: num(raw.users?.suspendedUsers ?? raw.users?.suspended),
    },
    services: {
      total: num(raw.services?.total),
      active: num(raw.services?.active),
      suspended: num(raw.services?.suspended),
      failed: num(raw.services?.failed),
    },
  };
}
